import { useEffect, useState } from "react";
import { CreditCard, LoaderCircle, ReceiptText } from "lucide-react";
import { api } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";

type Invoice = {
  id: string;
  number: string;
  status: string;
  currency: string;
  totalMicros: number;
  createdAt: string;
  dueAt: string | null;
};
type Billing = {
  account: {
    billingEmail: string | null;
    legalName: string | null;
    status: string;
    mollieMandateId: string | null;
  } | null;
  invoices: Invoice[];
  estimate: {
    rating: { totalBeforeCreditsMicros: number };
    period: { currency: string; endsAt: string };
  } | null;
  credits: Array<{
    id: string;
    remainingMicros: number;
    currency: string;
    reason: string;
  }>;
};

const amount = (micros: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
    micros / 1_000_000,
  );

export function BillingPage() {
  const [data, setData] = useState<Billing>();
  const [legalName, setLegalName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () =>
    api<Billing>("/organization/billing")
      .then((next) => {
        setData(next);
        setLegalName(next.account?.legalName ?? "");
        setBillingEmail(next.account?.billingEmail ?? "");
      })
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "Billing could not be loaded",
        ),
      );
  useEffect(() => {
    void load();
  }, []);
  const configure = async () => {
    setSaving(true);
    setError("");
    try {
      const result = await api<{ checkoutUrl: string | null }>(
        "/organization/billing/payment-method",
        {
          method: "POST",
          body: JSON.stringify({ legalName, billingEmail }),
        },
      );
      if (result.checkoutUrl) window.location.assign(result.checkoutUrl);
      else load();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Payment method could not be configured",
      );
    } finally {
      setSaving(false);
    }
  };
  const removePaymentMethod = async () => {
    if (!confirm(t("Cancel automatic collection and revoke this mandate?")))
      return;
    setSaving(true);
    setError("");
    try {
      await api("/organization/billing/payment-method", { method: "DELETE" });
      await load();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t("Mandate could not be revoked"),
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <SettingsPage
      title={t("Billing")}
      description={t(
        "Manage automatic payment, invoices, and billing status for this organization.",
      )}
    >
      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      {!data && !error && (
        <div className="grid min-h-64 place-items-center">
          <LoaderCircle className="animate-spin text-stone-400" />
        </div>
      )}
      {data && (
        <div className="space-y-6">
          {data.estimate && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Current period estimate")}</h2>
              <p className="mt-2 text-2xl font-bold text-lumen-700">
                {amount(
                  data.estimate.rating.totalBeforeCreditsMicros,
                  data.estimate.period.currency,
                )}
              </p>
              <p className="text-xs text-stone-500">
                {t(
                  "Estimated before available credits; this is not an invoice.",
                )}
              </p>
              {data.credits.length > 0 && (
                <p className="mt-2 text-sm text-stone-500">
                  {t("Available credits")}:{" "}
                  {amount(
                    data.credits.reduce(
                      (sum, credit) => sum + credit.remainingMicros,
                      0,
                    ),
                    data.credits[0].currency,
                  )}
                </p>
              )}
            </section>
          )}
          <section className="panel p-5">
            <div className="flex items-center gap-3">
              <CreditCard className="text-lumen-600" size={20} />
              <div>
                <h2 className="font-bold">{t("Payment method")}</h2>
                <p className="text-sm text-stone-500">
                  {t("Mollie recurring mandate status")}:{" "}
                  {data.account?.status ?? t("Not configured")}
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium">
                  {t("Legal name")}
                </span>
                <input
                  className="field w-full"
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium">
                  {t("Billing email")}
                </span>
                <input
                  className="field w-full"
                  type="email"
                  value={billingEmail}
                  onChange={(event) => setBillingEmail(event.target.value)}
                />
              </label>
            </div>
            <button
              className="button-primary mt-4"
              disabled={saving || !legalName || !billingEmail}
              onClick={() => void configure()}
            >
              {saving
                ? t("Saving…")
                : data.account?.mollieMandateId
                  ? t("Replace payment method")
                  : t("Set up automatic payment")}
            </button>
            {data.account?.mollieMandateId && (
              <button
                className="button mt-4 ml-2 text-red-600"
                disabled={saving}
                onClick={() => void removePaymentMethod()}
              >
                {t("Cancel automatic payment")}
              </button>
            )}
          </section>
          <section className="panel overflow-hidden">
            <div className="flex items-center gap-2 border-b border-stone-200 p-5 dark:border-white/10">
              <ReceiptText size={19} />
              <h2 className="font-bold">{t("Invoices")}</h2>
            </div>
            {data.invoices.length === 0 ? (
              <p className="p-5 text-sm text-stone-500">
                {t("No invoices yet.")}
              </p>
            ) : (
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {data.invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-4"
                  >
                    <div>
                      <div className="font-medium">{invoice.number}</div>
                      <div className="text-xs text-stone-500">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">
                        {amount(invoice.totalMicros, invoice.currency)}
                      </div>
                      <div className="text-xs tracking-wide text-stone-500 uppercase">
                        {invoice.status}
                      </div>
                      <a
                        className="text-xs text-lumen-600 hover:underline"
                        href={`/api/organization/billing/invoices/${invoice.id}?download=1`}
                      >
                        {t("Download invoice data")}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </SettingsPage>
  );
}
